export const UPCOMING_PRICE_CHANGE_DISABLED_TEXT =
  'Unavailable - upcoming price change'
export const UPCOMING_PRICE_CHANGE_WARNING_TEXT =
  'Products or family groups greyed out have upcoming price changes. Once the price change is live, you can create the promotion.'

export const hasUpcomingPriceChange = (product) =>
  product?.unavailableDueToUpcomingPriceChange === true ||
  product?.value?.unavailableDueToUpcomingPriceChange === true ||
  product?.hasPriceOrRrpImpactingChanges === true ||
  product?.value?.hasPriceOrRrpImpactingChanges === true

const hasDirectPriceOrRrpImpactingChange = (product) =>
  product?.hasPriceOrRrpImpactingChanges === true ||
  product?.value?.hasPriceOrRrpImpactingChanges === true

const productCodeFor = (product) =>
  product?.productCode || product?.value?.productCode || product?.value

const productValueFor = (product) => product?.value || product

const priceSnapshotFor = (product) => {
  const value = productValueFor(product)
  const regionalPriceMap = value?.regionalPriceMap || {}

  return Object.entries(regionalPriceMap)
    .flatMap(([pricingRegionCode, prices = []]) =>
      prices.map((price) => ({
        pricingRegionCode,
        price: price?.price,
        rrp: price?.rrp
      }))
    )
    .sort((left, right) => {
      if (left.pricingRegionCode !== right.pricingRegionCode) {
        return left.pricingRegionCode < right.pricingRegionCode ? -1 : 1
      }
      if (left.price !== right.price) return left.price < right.price ? -1 : 1
      if (left.rrp !== right.rrp) return left.rrp < right.rrp ? -1 : 1
      return 0
    })
}

const priceOrRrpValuesChanged = (product, liveProduct) => {
  if (!liveProduct) return hasDirectPriceOrRrpImpactingChange(product)

  const value = productValueFor(product)
  const liveValue = productValueFor(liveProduct)

  if (value?.status === 'LIVE') return true

  if (value?.packageQuantity !== liveValue?.packageQuantity) return true

  return (
    JSON.stringify(priceSnapshotFor(product)) !==
    JSON.stringify(priceSnapshotFor(liveProduct))
  )
}

const familyGroupProductCodes = (familyGroup) =>
  familyGroup?.productCodes ||
  familyGroup?.value?.productCodes ||
  familyGroup?.products?.map(productCodeFor).filter(Boolean) ||
  familyGroup?.value?.products?.map(productCodeFor).filter(Boolean) ||
  []

const familyGroupHasDirectUpcomingPriceChange = (familyGroup) =>
  hasUpcomingPriceChange(familyGroup) ||
  (familyGroup?.products || []).some(hasUpcomingPriceChange) ||
  (familyGroup?.value?.products || []).some(hasUpcomingPriceChange)

const familyGroupHasDirectPriceOrRrpImpactingChange = (familyGroup) =>
  hasDirectPriceOrRrpImpactingChange(familyGroup) ||
  (familyGroup?.products || []).some(hasDirectPriceOrRrpImpactingChange) ||
  (familyGroup?.value?.products || []).some(hasDirectPriceOrRrpImpactingChange)

export const productHasUpcomingPriceChange = (
  product,
  familyGroups = [],
  unavailableProductCodes = null
) => {
  if (hasUpcomingPriceChange(product)) return true

  const productCode = productCodeFor(product)
  if (!productCode) return false

  if (unavailableProductCodes instanceof Set) {
    return unavailableProductCodes.has(productCode)
  }

  return familyGroups.some(
    (familyGroup) =>
      familyGroupHasDirectUpcomingPriceChange(familyGroup) &&
      familyGroupProductCodes(familyGroup).includes(productCode)
  )
}

export const productHasUpcomingPriceChangeByCode = (products = [], code) =>
  products instanceof Set
    ? products.has(code)
    : products.some(
        (product) =>
          productCodeFor(product) === code && hasUpcomingPriceChange(product)
      )

export const buildUpcomingPriceChangeProductCodeSet = (
  products = [],
  familyGroups = []
) => {
  const liveProductsByCode = products.reduce((acc, product) => {
    const value = productValueFor(product)
    const code = productCodeFor(product)
    if (code && value?.status === 'LIVE') acc[code] = product
    return acc
  }, {})

  const productCodes = new Set(
    products
      .filter(
        (product) =>
          hasDirectPriceOrRrpImpactingChange(product) &&
          priceOrRrpValuesChanged(
            product,
            liveProductsByCode[productCodeFor(product)]
          )
      )
      .map(productCodeFor)
      .filter(Boolean)
  )

  familyGroups
    .filter((familyGroup) => {
      const familyProductCodes = familyGroupProductCodes(familyGroup)
      return (
        familyGroupHasDirectPriceOrRrpImpactingChange(familyGroup) ||
        familyProductCodes.some((code) => productCodes.has(code))
      )
    })
    .flatMap(familyGroupProductCodes)
    .filter(Boolean)
    .forEach((code) => productCodes.add(code))

  return productCodes
}

export const familyGroupHasUpcomingPriceChange = (
  familyGroup,
  products = []
) => {
  if (hasUpcomingPriceChange(familyGroup)) return true
  if (familyGroupHasDirectUpcomingPriceChange(familyGroup)) return true

  const productCodes = familyGroupProductCodes(familyGroup)
  if (!productCodes?.length) return false

  const unavailableProductCodes =
    products instanceof Set
      ? products
      : buildUpcomingPriceChangeProductCodeSet(products)

  return productCodes.some((code) =>
    productHasUpcomingPriceChangeByCode(unavailableProductCodes, code)
  )
}

export const withUpcomingPriceChangeDisabled = (
  option,
  isUnavailable = false
) => {
  const disabled = option.disabled || isUnavailable
  const value =
    isUnavailable && option.value && typeof option.value === 'object'
      ? {
          ...option.value,
          unavailableDueToUpcomingPriceChange: true,
          disabled: true
        }
      : option.value

  return {
    ...option,
    value,
    disabled,
    unavailableDueToUpcomingPriceChange:
      option.unavailableDueToUpcomingPriceChange || isUnavailable,
    text: isUnavailable
      ? `${option.text} (${UPCOMING_PRICE_CHANGE_DISABLED_TEXT})`
      : option.text
  }
}
